import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import API_ENDPOINTS from "../../utils/apiEndpoints";

const PaymentResponse = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [paymentData, setPaymentData] = useState(null);
  const [apiStatus, setApiStatus] = useState("loading"); // "loading", "success", "failed", "retrying"

  useEffect(() => {
    const storedData = sessionStorage.getItem("paymentData");
    if (storedData) {
      const parsedData = JSON.parse(storedData);
      setPaymentData(parsedData);
      sendPaymentData(parsedData); // Send data to API
    } else {
      setApiStatus("failed");
    }
  }, []);

  const sendPaymentData = async (paymentDetails) => {
    // Log payment details for debugging
    // console.log("Payment Details:", paymentDetails);
    // console.log("payment_id:", paymentDetails?.payment_id);
    // console.log("order_id:", paymentDetails?.order_id);
    // console.log("signature:", paymentDetails?.signature);

    try {
      // Retrieve and log the authentication token
      const token = localStorage.getItem("parentToken");
      // console.log("Token:", token);

      // Check if the token is present
      if (!token) {
        console.error("Authentication token is missing");
        throw new Error("Authentication token is missing");
      }

      // Validate payment details
      if (
        !paymentDetails?.payment_id || // Check for payment_id
        !paymentDetails?.order_id ||   // Check for order_id
        !paymentDetails?.signature     // Check for signature
      ) {
        console.error("Invalid payment details:", paymentDetails);
        throw new Error("Invalid payment details");
      }

      // Construct the data object to send to the backend
      const data = {
        student_id: Number(id),
        razorpay_payment_id: paymentDetails.payment_id, // Map payment_id to razorpay_payment_id
        razorpay_order_id: paymentDetails.order_id,     // Map order_id to razorpay_order_id
        razorpay_signature: paymentDetails.signature,   // Map signature to razorpay_signature
        status: paymentDetails.payment_id ? "success" : "failed", // Determine status
      };
      // console.log("Data to Send:", data);

      // Send the data to the backend API
      // console.log("Sending Data:", data);
      const response = await fetch(API_ENDPOINTS.SEND_PAYMENT_DATA, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      // Log the full response for debugging
      // console.log("Response Status:", response.status); // Log HTTP status code
      // console.log("Response Headers:", response.headers); // Log response headers

      // Parse and log the response body
      try {
        const responseBody = await response.json(); // Parse JSON response
        // console.log("Response Body:", responseBody); // Log response body
      } catch (jsonError) {
        console.error("Failed to parse response body as JSON:", jsonError);
        const responseText = await response.text(); // Fallback to text if JSON parsing fails
        // console.log("Response Body (Text):", responseText);
      }

      // Check if the response is not OK (e.g., 4xx or 5xx status)
      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json(); // Parse error response
        } catch (jsonError) {
          console.error("Failed to parse error response:", jsonError);
          throw new Error("Failed to parse error response");
        }
        console.error("API Error Response:", errorData);
        throw new Error(errorData.message || "API request failed");
      }

      // If everything is successful, set the API status to "success"
      setApiStatus("success");
    } catch (error) {
      // Handle any errors that occur during the process
      console.error("Payment API Error:", error.message);
      console.error("Error Details:", error);
      setApiStatus("failed");
    }
  };

  const handleRetry = () => {
    if (paymentData?.payment_status === "failed") {
      navigate(`/payment/${id}`); // Redirect for new payment
    } else {
      setApiStatus("retrying");
      setTimeout(() => {
        sendPaymentData(paymentData);
      }, 2000); // Retry after 2 seconds
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
      <div className="w-full max-w-2xl p-12 mx-4 text-center transition-all transform bg-white shadow-lg rounded-xl hover:shadow-xl">
        {/* Loader */}
        {apiStatus === "loading" || apiStatus === "retrying" ? (
          <div className="flex flex-col items-center">
            <svg className="w-12 h-12 animate-spin text-gray-600" viewBox="0 0 50 50">
              <circle cx="25" cy="25" r="20" stroke="currentColor" strokeWidth="4" fill="none" />
            </svg>
            <p className="mt-4 text-lg text-gray-600">Processing...</p>
          </div>
        ) : (
          <>
            {/* SVG - Success or Failure */}
            <div className="flex items-center justify-center w-24 h-24 mx-auto mb-8 rounded-full">
              {apiStatus === "success" ? (
                <div className="bg-green-100 rounded-full">
                  <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              ) : (
                <div className="bg-red-100 rounded-full p-4">
                  <svg className="w-16 h-16 text-red-600" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="32" cy="32" r="30" fill="none" stroke="currentColor" strokeWidth="4" />
                    <circle cx="22" cy="26" r="4" fill="currentColor" />
                    <circle cx="42" cy="26" r="4" fill="currentColor" />
                    <path d="M22 42c4-4 10-4 10-4s6 0 10 4" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
                  </svg>
                </div>
              )}
            </div>

            {/* Messages */}
            <h1 className={`mb-6 text-4xl font-extrabold ${apiStatus === "success" ? "text-green-600" : "text-red-600"}`}>
              {apiStatus === "failed" ? "Failed to Store Payment Details" : "Admission Successful!"}
            </h1>

            {/* Info Box */}
            <div className={`p-6 mb-8 rounded-lg ${apiStatus === "success" ? "bg-blue-50 text-blue-700" : "bg-red-50 text-red-700"}`}>
              <p className="text-lg font-medium">
                {apiStatus === "success"
                  ? `Your admission has been confirmed successfully.`
                  : "There was an issue storing your payment. Please try again."}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="mt-12 flex justify-center gap-4 flex-wrap">
              {/* Dashboard Button (Always Visible) */}
              <a href="/dashboard" className="px-8 py-4 text-lg font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700">
                Go to Dashboard
              </a>

              {/* Conditional Buttons */}
              {apiStatus === "failed" ? (
                // Retry Button (Visible only on failure)
                <button
                  onClick={handleRetry}
                  className="px-8 py-4 text-lg font-semibold text-white transition-colors duration-200 bg-red-600 rounded-lg hover:bg-red-700"
                >
                  Retry
                </button>
              ) : (
                // Download Admit Card Button (Visible only on success)
                <a href="/dashboard" className="px-8 py-4 text-lg font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700">
                  Download Admit Card
                </a>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentResponse;